import { useEffect, useState } from "react";
import "../styles/recent-messages.scss";
import { GuiSelect } from "../components/global-ui/gui-select";
import { useUpdateState } from "../utilities/utilities";
import VoiceMessagePlayer from "../components/voice-message-player";
import { useOnFirstLoad } from "../utilities/hooks";
import user from "../actions/user-actions";
import ToggleBar from "./ToggleBar";
import CircularProgress from "../actions/CircularProgress";
import { Path, removeTags, UserRoleName } from "../utilities/constants";
import navigate from "../utilities/navigation";
import { GuiButton } from "../components/global-ui/gui-button";
import { appState } from "../redux/redux-config";

export default function RecentMessages({
  userId,
  openPreview,
  isEditing,
  isComponentDisabled,
  showToastViewOnly,
  isUserViewOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [loaderError, setError] = useState(false);

  let [state, updateState] = useUpdateState(() => {
    return {
      messageType: 0,
      typeOptions: [
        { value: 0, label: "Show All" },
        { value: 1, label: "Voice" },
        { value: 2, label: "Email" },
        { value: 3, label: "SMS" },
      ],
      messages: [],
      tabState: "all",
    };
  });

  const currentUserType = appState().user?.userType;
  // Non-admin users (Level 1, Level 2, Faculty) see received messages on their dashboard
  const isNonAdminSelfView = !userId && currentUserType < UserRoleName.Admin;

  const { messageType } = state;
  useOnFirstLoad(() => {
    getMyMessages(messageType, false);
  });
  useEffect(() => {
    let timeoutId;

    if (loading) {
      // Set a timeout for 15 seconds
      timeoutId = setTimeout(() => {
        setLoading(false);
        setError(true);
      }, 15000);
    }

    // Cleanup the timeout on component unmount or when loading changes
    return () => clearTimeout(timeoutId);
  }, [loading]);
  async function getMyMessages(messageType, getOnlyUnreadMessages) {
    setError(false);
    try {
      let result;
      if (userId) {
        result = await user.getRecentMessagesForSpecificUser(
          messageType,
          getOnlyUnreadMessages,
          userId,
        );
      } else {
        result = await user.getRecentMessages(
          messageType,
          getOnlyUnreadMessages,
        );
      }
      if (result.success) {
        updateState({ messages: result.recentMessages });
        setLoading(false);
      } else {
        setError(true);
        setLoading(false);
      }
    } catch (error) {
      setError(true);
      setLoading(false);
    }
  }

  const onMessageTypeChange = (name, value) => {
    setLoading(true);
    updateState({ [name]: value });
    getMyMessages(value, state.tabState === "unread" ? true : false);
  };
  const formatDescription = (string) => {
    return string.split(" ").slice(0, 15).join(" ");
  };
  const formattedDate = (orgDate) => {
    const originalDate = new Date(orgDate);

    const yesterday = () => {
      let d = new Date();
      d.setDate(d.getDate() - 1);
      return d;
    };
    const currentDate = new Date().toISOString().split("T")[0];
    const yesterdayDate = yesterday().toISOString().split("T")[0];
    const scheduledDate = orgDate.split("T")[0];
    const month = originalDate.getMonth() + 1;
    const date = originalDate.getDate();
    const year = originalDate.getFullYear();

    const formattedTime = originalDate.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    if (scheduledDate === currentDate) {
      return `${formattedTime}`;
    } else if (scheduledDate === yesterdayDate) {
      return "Yesterday";
    } else {
      return `${month}/${date}/${year}`;
    }
  };

  const handleTabToggle = (tabState) => {
    if (tabState === "unread") {
      setLoading(true);
      getMyMessages(state.messageType, true);
      updateState({ tabState: tabState });
    } else {
      setLoading(true);
      getMyMessages(state.messageType, false);
      updateState({ tabState: tabState });
    }
  };
  const onMsgClick = (msg) => {
    const newMsg = { ...msg, dateTime: formattedDate(msg.dateTimeStamp) };
    openPreview(newMsg);
  };
  return (
    <>
      <div
        className={`messages-container ${loaderError && "loader-error"} ${
          (isEditing || isComponentDisabled) && "disabled"
        }`}
      >
        {loading && (
          <CircularProgress
            className="circular-progress--dashboard"
            label="Loading recent messages..."
          />
        )}
        {!loading && loaderError && (
          <span>Failed to load data, please try again later.</span>
        )}
        {!loading && !loaderError && (
          <>
            <div className="header">
              <span className="header-title">
                {state.messages.length > 0
                  ? "Recent Messages"
                  : "No Messages Yet"}
              </span>
              {state.messages.length > 0 && (
                <div className="filters">
                  <ToggleBar
                    onTabClick={handleTabToggle}
                    IsActiveTab={state.tabState}
                  ></ToggleBar>
                  <GuiSelect
                    className="messageType"
                    label="Message Type"
                    name="messageType"
                    value={state.messageType}
                    onChange={(name, option) =>
                      onMessageTypeChange(name, option.value)
                    }
                    options={state.typeOptions}
                  />
                </div>
              )}
            </div>

            {state.messages.length > 0 ? (
              <div className="messages-list">
                {state.messages.map((msg, index) => (
                  <div
                    key={index}
                    className="message-item"
                    onClick={() => onMsgClick(msg)}
                  >
                    <div className="avatar">
                      {msg.avatar ? (
                        <img src={msg.avatar} alt={`${msg.senderName}`} />
                      ) : (
                        <div className="initials">
                          {(() => {
                            const cleanName = (msg.senderName || "")
                              .replace(/[^a-zA-Z0-9\s]/g, "")
                              .replace(/\s+/g, " ")
                              .trim();

                            if (!cleanName) {
                              return (
                                <i
                                  className="fa fa-envelope"
                                  style={{ color: "#ccc", fontSize: "1.2em" }}
                                ></i>
                              );
                            }

                            const initials = cleanName
                              .split(" ")
                              .filter(Boolean)
                              .map((n) => n[0])
                              .slice(0, 2)
                              .join("")
                              .toUpperCase();

                            if (!initials) {
                              return (
                                <i
                                  className="fa fa-envelope"
                                  style={{ color: "#ccc", fontSize: "1.2em" }}
                                ></i>
                              );
                            }

                            return initials;
                          })()}
                        </div>
                      )}
                    </div>
                    <div className="message-content">
                      <div className="message-header">
                        <div className="d-flex-space-between">
                          <span className="message-header-name">
                            {msg.senderName}
                          </span>
                          <GuiButton
                            className="role-badge-messageType"
                            onClick={(e) => e.preventDefault()}
                          >
                            <span>
                              {msg.messageType === "SMS Inbound" ||
                              isNonAdminSelfView
                                ? "Received"
                                : "Sent"}
                            </span>
                          </GuiButton>
                        </div>
                        {msg.messageType === "Voice" ? (
                          <div className="voice-note">
                            <div className="message-preview">
                              <div
                                className={` ${
                                  msg.isMessageRead
                                    ? "message-preview-subject-isread"
                                    : "message-preview-subject-isunread"
                                }`}
                              >
                                {msg.messageType} – {msg.subject}
                              </div>

                              <div className="message-preview-time">
                                {formattedDate(msg.dateTimeStamp)}
                              </div>
                            </div>
                            <VoiceMessagePlayer message={msg} />
                          </div>
                        ) : (
                          <>
                            <div className="message-preview">
                              <div
                                className={` ${
                                  msg.isMessageRead
                                    ? "message-preview-subject-isread"
                                    : "message-preview-subject-isunread"
                                }`}
                              >
                                {msg.messageType} – {msg.subject}
                              </div>

                              <div className="message-preview-time">
                                {formattedDate(msg.dateTimeStamp)}
                              </div>
                            </div>
                            <span className="message-preview-content">
                              {removeTags(
                                formatDescription(msg.messageDescription),
                              )}
                              {" ..."}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="messages-list"
                style={{
                  height: 250,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <div className="message-item">
                  You have not sent or received any messages. When messages are
                  available, they will appear here
                </div>
              </div>
            )}

            <div className="recent-message-divider"></div>
            <div
              className="go-to-message-btn"
              style={
                isUserViewOnly
                  ? {
                      opacity: 0.6,
                    }
                  : {}
              }
              onClick={() => {
                if (isUserViewOnly) {
                  showToastViewOnly();
                } else {
                  if (userId) {
                    navigate(Path.PeopleMessages.replace(":id", userId));
                  } else {
                    navigate(Path.Messages);
                  }
                }
              }}
            >
              Go to Messages
              <i className=" go-to-message-icon far fa-chevron-right"></i>
            </div>
          </>
        )}
      </div>
    </>
  );
}
